import { memo, type ReactNode, type RefObject } from 'react';
import { Loader2 } from 'lucide-react';
import type { MediaClient, MediaConnectionState } from '@/features/meeting/media/contract';
import type { MeetingLayout, TilePlan, TileView } from '@/features/meeting/media/tilePlan';
import { GridPager } from '@/features/meeting/components/GridPager';
import { PresentationLayout } from '@/features/meeting/components/PresentationLayout';
import { VideoGrid } from '@/features/meeting/components/VideoGrid';

export interface CallStageProps {
  client: MediaClient;
  plan: TilePlan;
  layout: MeetingLayout;
  shareTile: TileView | null;
  pinnedId: string | null;
  state: MediaConnectionState;
  /** "X is presenting", with the take-over. See `PresenterBar`. */
  presenterBar?: ReactNode;
  /** Everyone in the room, so the pager can say "1-9 of 14". */
  totalParticipants: number;
  stageRef: RefObject<HTMLDivElement>;
  onPin: (participantId: string | null) => void;
  onPageChange: (page: number) => void;
}

const BANNER: Partial<Record<MediaConnectionState, string>> = {
  connecting: 'Connecting…',
  reconnecting: 'Reconnecting…',
  failed: 'Media could not connect. Check your camera and microphone permissions.',
};

/**
 * The call's video area. Deliberately the meeting's own layouts: a call and a
 * meeting differ in how they START, not in how a subscribed track is painted,
 * and a second grid implementation would be a second place for tiles to break.
 */
export const CallStage = memo(function CallStage(props: CallStageProps) {
  const banner = BANNER[props.state];

  return (
    <section aria-label="Call video" className="relative flex min-h-0 flex-1 flex-col gap-2 bg-stage p-2">
      {props.presenterBar}

      {banner === undefined ? null : (
        <p
          role="status"
          className="flex items-center justify-center gap-2 rounded-md bg-stage-tile px-3 py-1.5 text-base text-stage-foreground"
        >
          {props.state === 'failed' ? null : (
            <Loader2 size={16} className="animate-spin" aria-hidden />
          )}
          {banner}
        </p>
      )}

      <div ref={props.stageRef} className="relative min-h-0 flex-1">
        {props.layout === 'presentation' ? (
          <PresentationLayout
            tiles={props.plan.tiles}
            shareTile={props.shareTile}
            client={props.client}
            pinnedId={props.pinnedId}
            onPin={props.onPin}
          />
        ) : (
          <VideoGrid
            tiles={props.plan.tiles}
            client={props.client}
            pinnedId={props.pinnedId}
            onPin={props.onPin}
          />
        )}
      </div>

      {/* THE SAME PAGER THE MEETING USES, for the same reason: past
          `MAX_VISIBLE_TILES` the tiles are not merely off screen, their video is
          UNSUBSCRIBED by `buildTilePlan`. `page: 0` was hardcoded in
          `useCallRoom` and this file had no page code at all, so a call was
          capped at nine faces with no way to reach anybody behind them and
          nothing on screen saying they were there. It renders null while one
          page holds everybody, which is every call this product has ever
          carried. */}
      <GridPager
        page={props.plan.page}
        pageCount={props.plan.pageCount}
        pageSize={props.plan.pageSize}
        total={props.totalParticipants}
        onPageChange={props.onPageChange}
      />
    </section>
  );
});
